import React from "react";
import { Container, Row, Col, Card, Badge, Button } from "react-bootstrap";
import web5 from "../img/retail1.jpg";
import "../style/styles.css";
import { Helmet } from "react-helmet";

const Retails = () => {
  const alttext = "SaleSync retail POS billing and inventory management software";

  // ✅ Corporate hero styles (premium + readable + modern)
  const heroWrap = {
    borderRadius: 20,
    border: "1px solid #e8eef7",
    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.10)",
    overflow: "hidden",
    position: "relative",
    backgroundImage: `url(${web5})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const heroOverlay = {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(90deg, rgba(2,6,23,0.82) 0%, rgba(2,6,23,0.55) 45%, rgba(2,6,23,0.18) 100%)",
  };

  const heroGlow = {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(700px 320px at 18% 28%, rgba(56,189,248,0.22), transparent 55%), radial-gradient(650px 300px at 62% 30%, rgba(99,102,241,0.18), transparent 60%)",
    pointerEvents: "none",
  };

  const pill = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.22)",
    color: "#fff",
    fontWeight: 900,
    fontSize: 12.5,
    letterSpacing: "0.35px",
  };

  const heroTitle = {
    color: "#fff",
    fontWeight: 950,
    letterSpacing: "0.3px",
    fontSize: "clamp(34px, 4vw, 46px)",
    marginBottom: 8,
  };

  const heroSub = {
    color: "rgba(255,255,255,0.92)",
    fontWeight: 700,
    lineHeight: 1.7,
    fontSize: "clamp(14px, 1.6vw, 16px)",
    marginBottom: 18,
    maxWidth: 840,
  };

  const chip = {
    display: "inline-flex",
    alignItems: "center",
    padding: "9px 12px",
    borderRadius: 14,
    background: "rgba(255,255,255,0.10)",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#fff",
    fontWeight: 800,
    fontSize: 13,
  };

  const statBox = {
    borderRadius: 16,
    padding: "12px 14px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.16)",
    height: "100%",
  };

  // ✅ Page section styles
  const sectionCard = {
    borderRadius: 16,
    border: "1px solid #eef2f7",
    boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
  };

  const sectionTitle = {
    fontWeight: 900,
    color: "#0f172a",
    marginBottom: 8,
    letterSpacing: "0.2px",
  };

  const divider = {
    width: 72,
    height: 4,
    borderRadius: 12,
    background: "#ff6a00",
    marginBottom: 14,
  };

  const para = {
    textAlign: "justify",
    lineHeight: 1.9,
    color: "#1f2937",
    marginBottom: 12,
    fontSize: "1.02rem",
  };

  const highlightCard = {
    borderRadius: 16,
    border: "1px solid #e8eef7",
    boxShadow: "0 10px 24px rgba(0,0,0,0.05)",
    background: "linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%)",
  };

  // ✅ Product-focused, modern feature blocks (SEO-friendly headings)
  const coreFeatures = [
    {
      title: "Retail Billing (GST-ready POS)",
      points: [
        "Fast billing workflow with invoice printing",
        "Multi-payment support (cash/card/UPI as per setup)",
        "Automatic stock update after each sale",
        "Credit billing support with GST compliance",
      ],
    },
    {
      title: "Purchases & Inward Goods",
      points: [
        "Purchase billing with supplier-wise tracking",
        "Inward goods entry with real-time inventory update",
        "Better accuracy for stock valuation and reporting",
      ],
    },
    {
      title: "Inventory Management & Stock Control",
      points: [
        "Stock Summary & Closing Stock reporting",
        "Stock Adjustment to match physical vs system stock",
        "Physical Stock Taking support for periodic audits",
        "Minimum stock alerts to prevent stockouts",
        "High-value item tracking + Dead stock reporting",
        "Product Ledger (complete movement history)",
      ],
    },
    {
      title: "MIS Reports & Back Office Dashboard",
      points: [
        "Product sales reports (daily/monthly) for performance insights",
        "Graphical sales & purchase analysis (last 3 months trend view)",
        "Back-office dashboard for KPIs and operational visibility",
        "Minimum stock monitoring for smart procurement",
      ],
    },
    {
      title: "Security & Controls",
      points: [
        "Role-based access (user-to-user permissions)",
        "Better accountability for billing and stock actions",
        "Designed for secure day-to-day retail operations",
      ],
    },
  ];

  return (
    <>
      {/* ✅ SEO Meta Tags (Google-friendly) */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          SaleSync Retail POS Software | GST Billing & Inventory Management - EBS Software Solution
        </title>

        <meta
          name="description"
          content="SaleSync by EBS Software Solution is a GST-ready Retail POS software for billing, inventory management, purchases, stock control, MIS reports and role-based security. Ideal for retail outlets needing fast billing and real-time stock tracking."
        />

        {/* ✅ Canonical (change domain if needed) */}
        <link rel="canonical" href="https://www.ebssoftware.co.in/Retails" />

        {/* ✅ Social sharing (optional but good) */}
        <meta property="og:title" content="SaleSync Retail POS Software - EBS Software Solution" />
        <meta
          property="og:description"
          content="GST billing, inventory control, purchases, minimum stock alerts, MIS reports and secure role-based access for retail outlets."
        />
        <meta property="og:type" content="website" />

        {/* ✅ Keywords not necessary for Google, but fine */}
        <meta
          name="keywords"
          content="Retail POS software, Retail billing software, GST billing software, Inventory management software, Touchscreen POS, Retail ERP, Stock control software, SaleSync, EBS Software Solution"
        />
      </Helmet>

      {/* ✅ HERO */}
      <Container className="py-3">
        <div style={heroWrap} aria-label={alttext}>
          <div style={heroOverlay} />
          <div style={heroGlow} />

          <div style={{ position: "relative", padding: "34px 26px" }}>
            <div style={pill}>RETAIL • POS • INVENTORY</div>

            <h1 style={heroTitle}>SaleSync</h1>

            <p style={heroSub}>
              A modern, GST-ready Retail POS (Point of Sale) and Inventory Management
              System designed for faster billing, accurate stock control and actionable
              business insights—built for single stores and scalable operations.
            </p>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
              <span style={chip}>GST Billing</span>
              <span style={chip}>Touch / POS Ready</span>
              <span style={chip}>Inventory Control</span>
              <span style={chip}>MIS Reports</span>
              <span style={chip}>Role-based Security</span>
            </div>

            <Row className="g-3" style={{ maxWidth: 980 }}>
              <Col md={4}>
                <div style={statBox}>
                  <div style={{ color: "#fff", fontWeight: 950, marginBottom: 4 }}>
                    Faster Billing
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13.5, lineHeight: 1.55 }}>
                    Quick invoice generation with real-time stock updates.
                  </div>
                </div>
              </Col>

              <Col md={4}>
                <div style={statBox}>
                  <div style={{ color: "#fff", fontWeight: 950, marginBottom: 4 }}>
                    Accurate Inventory
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13.5, lineHeight: 1.55 }}>
                    Stock summary, adjustments, physical stock and alerts.
                  </div>
                </div>
              </Col>

              <Col md={4}>
                <div style={statBox}>
                  <div style={{ color: "#fff", fontWeight: 950, marginBottom: 4 }}>
                    Insights & Reports
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13.5, lineHeight: 1.55 }}>
                    MIS reports and dashboards to guide better decisions.
                  </div>
                </div>
              </Col>
            </Row>

            <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button
                variant="light"
                style={{ fontWeight: 900, borderRadius: 14, padding: "10px 14px" }}
                onClick={() => window.location.href = "/EmailContactForm"}
              >
                Request Demo
              </Button>

              <Button
                variant="outline-light"
                style={{ fontWeight: 900, borderRadius: 14, padding: "10px 14px" }}
                onClick={() => window.scrollTo({ top: 650, behavior: "smooth" })}
              >
                View Features
              </Button>
            </div>
          </div>
        </div>
      </Container>

      {/* ✅ CONTENT */}
      <Container className="py-4">
        {/* Overview */}
        <Row className="g-4">
          <Col lg={12}>
            <Card style={sectionCard}>
              <Card.Body style={{ padding: 22 }}>
                <h2 style={sectionTitle}>Overview</h2>
                <div style={divider} />
                <p style={para}>
                  <strong>SaleSync</strong> is an advanced retail billing and inventory
                  management system built to simplify daily POS operations and improve
                  stock accuracy. It supports GST-ready billing, purchase/inward
                  workflows, stock control and MIS reporting—helping retail businesses
                  run faster with better visibility and stronger operational control.
                </p>

                <Card style={highlightCard}>
                  <Card.Body style={{ padding: 16 }}>
                    <div style={{ fontWeight: 900, color: "#0f172a" }}>
                      Designed for modern retail operations
                    </div>
                    <div style={{ color: "#334155", marginTop: 6, lineHeight: 1.7 }}>
                      Faster billing • Real-time stock • Alerts & analytics • Secure access
                    </div>
                  </Card.Body>
                </Card>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Feature Sections */}
        <Row className="mt-4">
          <Col lg={12}>
            <h2 style={sectionTitle}>Key Features</h2>
            <div style={divider} />
          </Col>
        </Row>

        <Row className="g-4">
          {coreFeatures.map((block, idx) => (
            <Col lg={6} key={idx}>
              <Card style={sectionCard}>
                <Card.Body style={{ padding: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <Badge bg="primary" style={{ borderRadius: 12, padding: "8px 10px", fontWeight: 900 }}>
                      {idx + 1}
                    </Badge>
                    <h3 style={{ margin: 0, fontWeight: 900, color: "#0f172a", fontSize: 18 }}>
                      {block.title}
                    </h3>
                  </div>

                  <ul style={{ marginBottom: 0, paddingLeft: "1.1rem", lineHeight: 1.8 }}>
                    {block.points.map((p, i) => (
                      <li key={i} style={{ color: "#1f2937", marginBottom: 6 }}>
                        {p}
                      </li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Conclusion */}
        <Row className="mt-4">
          <Col lg={12}>
            <Card style={highlightCard}>
              <Card.Body style={{ padding: 18 }}>
                <h2 style={{ ...sectionTitle, marginBottom: 6 }}>Why choose SaleSync?</h2>
                <div style={divider} />
                <p style={{ ...para, marginBottom: 0 }}>
                  SaleSync helps retail outlets manage billing, purchases and inventory with
                  better accuracy and control. With minimum stock alerts, stock reconciliation tools
                  and MIS reporting, businesses can reduce stock issues, improve profitability and
                  make faster decisions—while maintaining secure access through role-based permissions.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Retails;
